import React, { useState } from "react";
import { createListing } from "../lib/listings";
import { T } from "../theme";
import { Badge, Btn, Card, Input, SectionTitle, Select, TapeIcon } from "../ui";

const TYPES = ["sale", "trade", "both"];

export default function CreateListingScreen({ ownerUserId, sellerDisplayName, tape, onDone, onCancel }) {
  const [listingType, setListingType] = useState("sale");
  const [price, setPrice] = useState(tape.estimatedValue ?? "");
  const [tradeNotes, setTradeNotes] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    setError("");
    if ((listingType === "sale" || listingType === "both") && price === "") {
      setError("Enter a price for a sale listing.");
      return;
    }
    if (!description.trim()) {
      setError("Add a description with how buyers should contact you.");
      return;
    }
    setSaving(true);
    try {
      await createListing(ownerUserId, {
        sellerDisplayName: sellerDisplayName || "Collector",
        tapeId: tape.id,
        artist: tape.artist,
        albumTitle: tape.albumTitle,
        releaseYear: tape.releaseYear || null,
        condition: tape.condition || null,
        cartridgeColor: tape.cartridgeColor || null,
        variation: tape.variation || null,
        mainPhotoUrl: tape.mainPhotoUrl || null,
        listingType,
        price: price !== "" ? Number(price) : null,
        currencyCode: "USD",
        tradeNotes: listingType !== "sale" ? tradeNotes || null : null,
        description: description.trim(),
      });
      onDone();
    } catch (e) {
      setError("Could not create this listing. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Card style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {tape.mainPhotoUrl ? (
          <img src={tape.mainPhotoUrl} alt={tape.albumTitle} style={{ width: 56, height: 56, objectFit: "cover", borderRadius: 8 }} />
        ) : (
          <TapeIcon size={48} />
        )}
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: "bold", color: T.text }}>{tape.albumTitle}</div>
          <div style={{ fontSize: 12, color: T.muted }}>{tape.artist}</div>
          {tape.condition && <Badge condition={tape.condition} />}
        </div>
      </Card>

      {error && <div style={{ color: "#C0392B", fontSize: 12, marginBottom: 10 }}>{error}</div>}

      <SectionTitle>Listing Details</SectionTitle>
      <Select label="Type" value={listingType} onChange={(e) => setListingType(e.target.value)} options={TYPES} />
      {(listingType === "sale" || listingType === "both") && (
        <Input label="Price ($)" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="0.00" type="number" />
      )}
      {(listingType === "trade" || listingType === "both") && (
        <Input label="What would you trade for?" value={tradeNotes} onChange={(e) => setTradeNotes(e.target.value)} placeholder="e.g. looking for other Motown 8-tracks" />
      )}
      <div style={{ marginBottom: 10 }}>
        <label style={{ fontSize: 9, color: T.muted, letterSpacing: "0.18em", textTransform: "uppercase", display: "block", marginBottom: 3 }}>Description &amp; Contact Info</label>
        <textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe the item and how buyers can reach you (email, social handle, etc.) — there's no in-app messaging yet." style={{ width: "100%", background: T.inputBg, border: `1px solid ${T.border}`, borderRadius: 6, color: T.text, padding: "9px 11px", fontSize: 13, fontFamily: T.font, outline: "none", boxSizing: "border-box", minHeight: 90, resize: "vertical" }} />
      </div>

      <div style={{ display: "flex", gap: 10, marginTop: 6 }}>
        <Btn variant="outline" onClick={onCancel} style={{ flex: 1 }}>Cancel</Btn>
        <Btn disabled={saving} onClick={save} style={{ flex: 2 }}>{saving ? "Posting…" : "Post Listing"}</Btn>
      </div>
      <div style={{ height: 20 }} />
    </div>
  );
}
